import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {Organization} from 'orgs/OrganizationConstants';
import QueryInput from 'search/QueryInput';
import {SavedSearchesQuery} from 'search/saved-searches/__generated/SavedSearchesQuery';
import {SAVED_SEARCHES_QUERY} from 'search/saved-searches/SavedSearchesQuery';
import {SavedSearchModalHelpScreen} from 'search/saved-searches/SavedSearchModalHelpScreen';
import {SavedSearchModalLoadingScreen} from 'search/saved-searches/SavedSearchModalLoadingScreen';
import SavedSearchModalMenuSection from 'search/saved-searches/SavedSearchModalMenuSection';
import SavedSearchModalOptions from 'search/saved-searches/SavedSearchModalOptions';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import {WHITE} from 'ui-library/Colors';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import Modal, {ModalBodyColor, ModalButton} from 'ui-library/modals/Modal';
import sortedBy from 'util/sortedBy';

export interface BaseSavedSearchModalProps {
  searchSection: SearchSection;
  closeModal: () => void;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  showModalOptions?: boolean;
  showOptionsMenu?: boolean;
  buttons?: Array<ModalButton> | null;
  onRequestSubmit?: (value: SavedSearch) => void;
  selectedValue?: string;
  setSelectedValue?: (value: string) => void;
  onSelect?: (value: OptionValue) => void;
}

/** A modal for viewing and managing all Saved Searches. */
export default function BaseSavedSearchModal(props: BaseSavedSearchModalProps): JSX.Element {
  const [query, setQuery] = useState('');
  const savedSearchesResult = useBenchlingQuery<SavedSearchesQuery>(SAVED_SEARCHES_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  useEffect(() => {
    savedSearchesResult.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let mySavedSearchData: Array<OptionData> = [];
  let myOrgsSavedSearchData: Array<OptionData> = [];
  let sharedSavedSearchData: Array<OptionData> = [];
  let sortedSavedSearches: Array<SavedSearch> = [];

  if (savedSearchesResult.type === QueryResultType.SUCCESS) {
    ({mySavedSearchData, myOrgsSavedSearchData, sharedSavedSearchData, sortedSavedSearches} =
      sortSavedSearches(savedSearchesResult.data.savedSearches));
  } else if (savedSearchesResult.type === QueryResultType.PENDING) {
    return <SavedSearchModalLoadingScreen closeModal={props.closeModal} />;
  }

  if (
    mySavedSearchData.length === 0 &&
    myOrgsSavedSearchData.length === 0 &&
    sharedSavedSearchData.length === 0
  ) {
    return <SavedSearchModalHelpScreen closeModal={props.closeModal} />;
  } else {
    return (
      <Modal
        onRequestClose={props.closeModal}
        title="Saved Searches"
        mediumLarge={true}
        fullHeight={true}
        modalBodyBgColor={ModalBodyColor.GRAY_LIGHTER}
        onRequestSubmit={
          props.onRequestSubmit
            ? () => {
                const selectedSearch = sortedSavedSearches.find((ss) => ss.id === props.selectedValue);
                assert(selectedSearch !== undefined, 'Selected value should be displayed saved search');
                props.onRequestSubmit!(selectedSearch);
              }
            : null
        }
        buttons={props.buttons}
      >
        <div>
          <div className={css(styles.queryRow)}>
            <div className={css(styles.query)}>
              <QueryInput
                onUpdateQuery={(query: string) => {
                  setQuery(query);
                }}
                placeholder="Find Saved Searches"
                query={query}
              />
            </div>
            {props.showModalOptions && (
              <SavedSearchModalOptions
                savedSearchOptionData={mySavedSearchData.concat(sharedSavedSearchData)}
              />
            )}
          </div>
          <SavedSearchModalMenuSection
            savedSearchOptionData={getMatchingOptionDatas(query, mySavedSearchData)}
            title="My Searches"
            refetchSavedSearches={async () => {
              await savedSearchesResult.refetch();
            }}
            {...props}
          />
          {myOrgsSavedSearchData.length > 0 && (
            <SavedSearchModalMenuSection
              savedSearchOptionData={getMatchingOptionDatas(query, myOrgsSavedSearchData)}
              title="My Organizations' Searches"
              refetchSavedSearches={async () => {
                await savedSearchesResult.refetch();
              }}
              {...props}
            />
          )}
          <SavedSearchModalMenuSection
            savedSearchOptionData={getMatchingOptionDatas(query, sharedSavedSearchData)}
            title="Shared With Me"
            refetchSavedSearches={async () => {
              await savedSearchesResult.refetch();
            }}
            {...props}
          />
        </div>
      </Modal>
    );
  }
}

function sortSavedSearches(savedSearches: Array<SavedSearch>): {
  mySavedSearchData: Array<OptionData>;
  myOrgsSavedSearchData: Array<OptionData>;
  sharedSavedSearchData: Array<OptionData>;
  sortedSavedSearches: Array<SavedSearch>;
} {
  const mySavedSearchData: Array<OptionData> = [];
  const myOrgsSavedSearchData: Array<OptionData> = [];
  const sharedSavedSearchData: Array<OptionData> = [];
  const sortedSavedSearches = sortedBy(savedSearches, ['name']);
  for (const savedSearch of sortedSavedSearches) {
    let disabled;
    if (EntityStore.currentUser() && savedSearch.ownerJson.id === EntityStore.currentUser().id) {
      disabled = false;
    } else {
      disabled = !savedSearch.hasAccessToFiltersAndSection;
    }

    const data = {
      disabled,
      name: savedSearch.name,
      pureTextName: savedSearch.name,
      value: savedSearch,
      className: css(styles.savedSearchOptionData),
    };
    if (savedSearch.permissions.owner) {
      mySavedSearchData.push(data);
    } else if (isCurrentUserAdmin(savedSearch.ownerJson.id)) {
      myOrgsSavedSearchData.push(data);
    } else {
      sharedSavedSearchData.push(data);
    }
  }

  return {mySavedSearchData, myOrgsSavedSearchData, sharedSavedSearchData, sortedSavedSearches};
}

function isCurrentUserAdmin(orgId: string): boolean {
  const currentUser = EntityStore.currentUser();
  return (
    currentUser &&
    currentUser.organizations &&
    currentUser.organizations.some((org: Organization) => org.id === orgId && org.admin)
  );
}

const styles = StyleSheet.create({
  queryRow: {
    display: 'flex',
    gap: '10px',
  },
  query: {
    display: 'flex',
    marginBottom: 13,
    background: WHITE,
    width: 412,
  },
  savedSearchOptionData: {
    overflow: 'hidden',
  },
});
